import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiPathEmailSubscriptionStatus, apiPathManageSubscription, fetcher } from "./queryHelpers";

/**
 * A hook for returning the subscription status for a given message ID.
 * @param messageId - The ID of the message to get the subscription status for.
 * @returns The subscription status for the given message ID, as an object with the following properties:
 * - emailAddress: The email address of the recipient
 * - hasUnsubscribed: A boolean indicating whether the recipient has unsubscribed from the email
 * - clientName: The name of the client that sent the email
 * - candidateId: The ID of the candidate that the email was sent to
 * @returns UseQueryResult<ISubscriptionStatusResponse>
 */
export const useSubscriptionStatus = (messageId: string | undefined) => {
  return useQuery<ISubscriptionStatusResponse, Error, ISubscriptionStatusResponse>(
    ["subscriptionStatus", messageId],
    () => fetcher(apiPathEmailSubscriptionStatus + messageId),
    {
      enabled: !!messageId,
      staleTime: Infinity,
    }
  );
};

/**
 * A hook for updating the subscription status for a given candidate ID. Will reset the subscription status query cache on success.
 * @example
 * const [mutate, { isLoading, error }] = useChangeSubscriptionStatus();
 * function unsubscribe() {
 *  mutate({ candidateId: "123", subscribed: false });
 * }
 */
export const useChangeSubscriptionStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (command: IManageSubscriptionCommand) =>
      fetcher(apiPathManageSubscription, {
        method: "PUT",
        body: JSON.stringify(command),
        headers: { "Content-Type": "application/json" },
      }),
    {
      onError: (error) => console.error(error),
      onSuccess: () => queryClient.invalidateQueries(["subscriptionStatus"]),
    }
  );
};

interface ISubscriptionStatusResponse {
  emailAddress: string;
  hasUnsubscribed: boolean;
  clientName: string;
  candidateId: string;
}

interface IManageSubscriptionCommand {
  candidateId: string;
  messageId: string;
  subscribed: boolean;
}
